import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AthleteView, PageResponse, SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { AthletePaymentSection } from './AthletePaymentSection';
import { AthleteStatusControls } from './AthleteStatusControls';
import { ClubMembershipSection } from './ClubMembershipSection';
import { ChangeRequestSection } from './ChangeRequestSection';
import { HockeyProfileSection } from './HockeyProfileSection';
import {
  athleteDisplayName,
  athleteListPath,
  athleteStatusLabelKeys,
  changesFromForm,
  formFromAthlete,
  formatCalendarDate,
  hasChanges,
  type AthleteForm,
} from './athletes';
import { RowActionIcon, type RowAction } from './RowActionIcon';

export interface AthletesPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Whether the person may add and correct athletes; the server enforces it as well. */
  canManage: boolean;
  /** Whether the person may see club membership periods; the server enforces it as well. */
  canViewMemberships: boolean;
  /** A guardian may propose changes to a linked child's record. */
  canRequestChanges: boolean;
  /** Finance (`billing.manage`) may record a payment on the athlete's billing account. */
  canRecordPayments: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

/** Which dialog is open; row dialogs keep the athlete id so a reload shows the fresh record. */
type OpenDialog = { kind: 'create' } | { kind: RowAction; athleteId: string };

const emptyForm: AthleteForm = {
  givenName: '',
  familyName: '',
  dateOfBirth: '',
  email: '',
  phone: '',
};

const actionLabelKeys: Readonly<Record<RowAction, TranslationKey>> = {
  details: 'athletes.action.details',
  edit: 'athletes.edit',
  status: 'athletes.action.status',
  payment: 'athletes.action.payment',
};

/** Contact columns need room; on a phone the row keeps name, status and the actions. */
const wideColumn = { display: { xs: 'none', md: 'table-cell' } } as const;
const mediumColumn = { display: { xs: 'none', sm: 'table-cell' } } as const;

/** Dense rows: tighter cell padding, small icon buttons and a short status chip. */
const compactTable = {
  '& .MuiTableCell-root': { py: 0.25, lineHeight: 1.3 },
  '& .MuiTableCell-head': { py: 0.75 },
  '& .MuiIconButton-root': { padding: 0.5 },
  '& .MuiChip-root': { height: 20 },
} as const;

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

function isDuplicateWarning(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'ATHLETE_DUPLICATE_CANDIDATE';
}

export function AthletesPanel({
  locale,
  organizationId,
  csrfToken,
  canManage,
  canViewMemberships,
  canRequestChanges,
  canRecordPayments,
}: AthletesPanelProps): React.JSX.Element {
  const [athletes, setAthletes] = useState<AthleteView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [saved, setSaved] = useState(false);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const base = `/api/v1/organizations/${organizationId}`;

  const load = useCallback(
    async (search: string, cursor: string | null) => {
      try {
        const page = await apiRequest<PageResponse<AthleteView>>(
          athleteListPath(base, { search, cursor }),
        );
        setAthletes((current) =>
          cursor === null ? page.items : [...(current ?? []), ...page.items],
        );
        setNextCursor(page.nextCursor);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    void load(activeSearch, null);
  }, [load, activeSearch]);

  async function create(form: AthleteForm, confirmDuplicate: boolean): Promise<boolean> {
    setErrorKey(null);
    setSaved(false);
    try {
      await apiRequest<AthleteView>(`${base}/athletes`, {
        method: 'POST',
        body: { ...form, confirmDuplicate },
        csrfToken,
      });
      setSaved(true);
      await load(activeSearch, null);
      return true;
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      if (isDuplicateWarning(error)) throw error;
      return false;
    }
  }

  async function save(athlete: AthleteView, form: AthleteForm): Promise<boolean> {
    setErrorKey(null);
    setSaved(false);
    const changes = changesFromForm(athlete, form);
    if (!hasChanges(changes)) return true;
    try {
      await apiRequest<AthleteView>(`${base}/athletes/${athlete.id}`, {
        method: 'PATCH',
        body: changes,
        csrfToken,
        ifMatchVersion: athlete.version,
      });
      setSaved(true);
      await load(activeSearch, null);
      return true;
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      return false;
    }
  }

  function openDialog(next: OpenDialog): void {
    setErrorKey(null);
    setSaved(false);
    setDialog(next);
  }

  function closeDialog(): void {
    setErrorKey(null);
    setDialog(null);
  }

  const errorAlert = errorKey !== null && (
    <Alert
      severity={errorKey === 'error.athlete.duplicateCandidate' ? 'warning' : 'error'}
      sx={{ mb: 2 }}
    >
      {translate(locale, errorKey)}
    </Alert>
  );
  const dialogAthlete =
    dialog !== null && dialog.kind !== 'create'
      ? athletes?.find((athlete) => athlete.id === dialog.athleteId)
      : undefined;
  const rowActions: RowAction[] = [
    'details',
    ...(canManage ? (['edit', 'status'] as const) : []),
    ...(canRecordPayments ? (['payment'] as const) : []),
  ];

  return (
    <Box component="section" aria-labelledby="athletes-title" sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h6" component="h2" id="athletes-title" sx={{ flexGrow: 1 }}>
          {translate(locale, 'nav.athletes')}
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            onClick={() => {
              openDialog({ kind: 'create' });
            }}
          >
            {translate(locale, 'athletes.create.title')}
          </Button>
        )}
      </Box>
      <Box
        component="form"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setActiveSearch(searchText.trim());
        }}
        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}
      >
        <TextField
          size="small"
          type="search"
          label={translate(locale, 'athletes.search.label')}
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <Button type="submit" variant="outlined">
          {translate(locale, 'athletes.search.submit')}
        </Button>
      </Box>
      {dialog === null && errorAlert}
      {saved && errorKey === null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'athletes.saved')}
        </Alert>
      )}
      {athletes === null && errorKey === null && (
        <Typography>{translate(locale, 'athletes.loading')}</Typography>
      )}
      {athletes?.length === 0 && <Typography>{translate(locale, 'athletes.empty')}</Typography>}
      {athletes !== null && athletes.length > 0 && (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small" aria-labelledby="athletes-title" sx={compactTable}>
            <TableHead>
              <TableRow>
                <TableCell>{translate(locale, 'athletes.table.name')}</TableCell>
                <TableCell sx={mediumColumn}>{translate(locale, 'athletes.dateOfBirth')}</TableCell>
                {canManage && (
                  <TableCell sx={wideColumn}>{translate(locale, 'athletes.email')}</TableCell>
                )}
                {canManage && (
                  <TableCell sx={wideColumn}>{translate(locale, 'athletes.phone')}</TableCell>
                )}
                <TableCell>{translate(locale, 'athletes.table.status')}</TableCell>
                <TableCell align="right">{translate(locale, 'athletes.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {athletes.map((athlete) => {
                const name = athleteDisplayName(athlete, locale);
                return (
                  <TableRow key={athlete.id} hover>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      {name}
                    </TableCell>
                    <TableCell sx={mediumColumn}>
                      {formatCalendarDate(athlete.dateOfBirth, locale)}
                    </TableCell>
                    {canManage && <TableCell sx={wideColumn}>{athlete.email ?? ''}</TableCell>}
                    {canManage && <TableCell sx={wideColumn}>{athlete.phone ?? ''}</TableCell>}
                    <TableCell>
                      <Chip
                        size="small"
                        label={translate(locale, athleteStatusLabelKeys[athlete.status])}
                        color={athlete.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {rowActions.map((action) => {
                        const label = `${translate(locale, actionLabelKeys[action])}: ${name}`;
                        return (
                          <Tooltip key={action} title={translate(locale, actionLabelKeys[action])}>
                            <IconButton
                              size="small"
                              aria-label={label}
                              onClick={() => {
                                openDialog({ kind: action, athleteId: athlete.id });
                              }}
                            >
                              <RowActionIcon action={action} />
                            </IconButton>
                          </Tooltip>
                        );
                      })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {nextCursor !== null && (
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            void load(activeSearch, nextCursor);
          }}
        >
          {translate(locale, 'athletes.loadMore')}
        </Button>
      )}
      <Dialog
        open={dialog !== null && (dialog.kind === 'create' || dialogAthlete !== undefined)}
        onClose={closeDialog}
        fullWidth
        maxWidth={dialog?.kind === 'details' || dialog?.kind === 'payment' ? 'md' : 'sm'}
        aria-labelledby="athletes-dialog-title"
      >
        {dialog?.kind === 'create' && canManage && (
          <CreateAthleteForm
            locale={locale}
            errorAlert={errorAlert}
            onCreate={async (form, confirmDuplicate) => {
              const created = await create(form, confirmDuplicate);
              if (created) setDialog(null);
              return created;
            }}
            onCancel={closeDialog}
          />
        )}
        {dialog?.kind === 'edit' && dialogAthlete !== undefined && canManage && (
          <EditAthleteForm
            key={dialogAthlete.id}
            locale={locale}
            athlete={dialogAthlete}
            errorAlert={errorAlert}
            onSave={async (form) => {
              const done = await save(dialogAthlete, form);
              if (done) setDialog(null);
            }}
            onCancel={closeDialog}
          />
        )}
        {dialog?.kind === 'status' && dialogAthlete !== undefined && canManage && (
          <>
            <DialogTitle id="athletes-dialog-title">
              {translate(locale, 'athletes.action.status')}:{' '}
              {athleteDisplayName(dialogAthlete, locale)}
            </DialogTitle>
            <DialogContent>
              <AthleteStatusControls
                key={dialogAthlete.id}
                locale={locale}
                base={base}
                csrfToken={csrfToken}
                athlete={dialogAthlete}
                onChanged={async () => {
                  await load(activeSearch, null);
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{translate(locale, 'athletes.dialog.close')}</Button>
            </DialogActions>
          </>
        )}
        {dialog?.kind === 'payment' && dialogAthlete !== undefined && canRecordPayments && (
          <>
            <DialogTitle id="athletes-dialog-title">
              {translate(locale, 'athletes.action.payment')}:{' '}
              {athleteDisplayName(dialogAthlete, locale)}
            </DialogTitle>
            <DialogContent>
              <AthletePaymentSection
                key={dialogAthlete.id}
                locale={locale}
                organizationId={organizationId}
                csrfToken={csrfToken}
                athleteId={dialogAthlete.id}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{translate(locale, 'athletes.dialog.close')}</Button>
            </DialogActions>
          </>
        )}
        {dialog?.kind === 'details' && dialogAthlete !== undefined && (
          <>
            <DialogTitle
              id="athletes-dialog-title"
              sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              <Box component="span" sx={{ flexGrow: 1 }}>
                {athleteDisplayName(dialogAthlete, locale)}
              </Box>
              {canRecordPayments && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RowActionIcon action="payment" />}
                  onClick={() => {
                    openDialog({ kind: 'payment', athleteId: dialogAthlete.id });
                  }}
                >
                  {translate(locale, 'athletes.action.payment')}
                </Button>
              )}
            </DialogTitle>
            <DialogContent>
              <AthleteDetails
                locale={locale}
                athlete={dialogAthlete}
                base={base}
                csrfToken={csrfToken}
                canManage={canManage}
                canViewMemberships={canViewMemberships}
                canRequestChanges={canRequestChanges && !canManage}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{translate(locale, 'athletes.dialog.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

interface AthleteFieldsProps {
  locale: SupportedLocale;
  form: AthleteForm;
  onChange: (form: AthleteForm) => void;
}

function AthleteFields({ locale, form, onChange }: AthleteFieldsProps): React.JSX.Element {
  const field = (name: keyof AthleteForm) => ({
    value: form[name],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...form, [name]: event.target.value });
    },
  });
  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <TextField
        size="small"
        required
        label={translate(locale, 'athletes.givenName')}
        {...field('givenName')}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
      <TextField
        size="small"
        required
        label={translate(locale, 'athletes.familyName')}
        {...field('familyName')}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
      <TextField
        size="small"
        required
        type="date"
        label={translate(locale, 'athletes.dateOfBirth')}
        {...field('dateOfBirth')}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        size="small"
        type="email"
        label={translate(locale, 'athletes.email')}
        {...field('email')}
        slotProps={{ htmlInput: { maxLength: 320 } }}
      />
      <TextField
        size="small"
        type="tel"
        label={translate(locale, 'athletes.phone')}
        {...field('phone')}
        slotProps={{ htmlInput: { maxLength: 40 } }}
      />
    </Stack>
  );
}

interface AthleteDetailsProps {
  locale: SupportedLocale;
  athlete: AthleteView;
  base: string;
  csrfToken: string;
  canManage: boolean;
  canViewMemberships: boolean;
  canRequestChanges: boolean;
}

function AthleteDetails({
  locale,
  athlete,
  base,
  csrfToken,
  canManage,
  canViewMemberships,
  canRequestChanges,
}: AthleteDetailsProps): React.JSX.Element {
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label={translate(locale, athleteStatusLabelKeys[athlete.status])}
          color={athlete.status === 'active' ? 'success' : 'default'}
        />
        <Typography color="text.secondary">
          {translate(locale, 'athletes.dateOfBirth')}:{' '}
          {formatCalendarDate(athlete.dateOfBirth, locale)}
        </Typography>
      </Box>
      {athlete.email !== undefined && athlete.email !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'athletes.email')}: {athlete.email}
        </Typography>
      )}
      {athlete.phone !== undefined && athlete.phone !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'athletes.phone')}: {athlete.phone}
        </Typography>
      )}
      {canViewMemberships && (
        <ClubMembershipSection
          key={athlete.version}
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          athleteId={athlete.id}
          canManage={canManage}
        />
      )}
      <HockeyProfileSection
        key={`profile-${String(athlete.version)}`}
        locale={locale}
        base={base}
        csrfToken={csrfToken}
        athleteId={athlete.id}
        canManage={canManage}
      />
      {canRequestChanges && (
        <ChangeRequestSection
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          athleteId={athlete.id}
        />
      )}
    </>
  );
}

interface EditAthleteFormProps {
  locale: SupportedLocale;
  athlete: AthleteView;
  errorAlert: React.ReactNode;
  onSave: (form: AthleteForm) => Promise<void>;
  onCancel: () => void;
}

function EditAthleteForm({
  locale,
  athlete,
  errorAlert,
  onSave,
  onCancel,
}: EditAthleteFormProps): React.JSX.Element {
  const [form, setForm] = useState<AthleteForm>(() => formFromAthlete(athlete));
  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(form);
      }}
    >
      <DialogTitle id="athletes-dialog-title">
        {translate(locale, 'athletes.edit')}: {athleteDisplayName(athlete, locale)}
      </DialogTitle>
      <DialogContent>
        {errorAlert}
        <AthleteFields locale={locale} form={form} onChange={setForm} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{translate(locale, 'athletes.edit.cancel')}</Button>
        <Button type="submit" variant="contained">
          {translate(locale, 'athletes.edit.save')}
        </Button>
      </DialogActions>
    </Box>
  );
}

interface CreateAthleteFormProps {
  locale: SupportedLocale;
  errorAlert: React.ReactNode;
  /** Rejects when the server warns about a possible duplicate, so the form can offer to confirm. */
  onCreate: (form: AthleteForm, confirmDuplicate: boolean) => Promise<boolean>;
  onCancel: () => void;
}

function CreateAthleteForm({
  locale,
  errorAlert,
  onCreate,
  onCancel,
}: CreateAthleteFormProps): React.JSX.Element {
  const [form, setForm] = useState<AthleteForm>(emptyForm);
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  function submit(confirmDuplicate: boolean): void {
    onCreate(form, confirmDuplicate).then(
      (created) => {
        if (created) {
          setForm(emptyForm);
          setDuplicateWarning(false);
        }
      },
      () => {
        setDuplicateWarning(true);
      },
    );
  }

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        submit(false);
      }}
    >
      <DialogTitle id="athletes-dialog-title">
        {translate(locale, 'athletes.create.title')}
      </DialogTitle>
      <DialogContent>
        {errorAlert}
        <AthleteFields
          locale={locale}
          form={form}
          onChange={(next) => {
            setForm(next);
            setDuplicateWarning(false);
          }}
        />
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onCancel}>{translate(locale, 'athletes.edit.cancel')}</Button>
        {duplicateWarning && (
          <Button
            color="warning"
            variant="outlined"
            onClick={() => {
              submit(true);
            }}
          >
            {translate(locale, 'athletes.create.anyway')}
          </Button>
        )}
        <Button type="submit" variant="contained">
          {translate(locale, 'athletes.create.submit')}
        </Button>
      </DialogActions>
    </Box>
  );
}
